/**
 * Componente NotificationBell - Ícone de sino com badge de notificações
 * 
 * Exibe um ícone de sino clicável com contador de notificações não lidas.
 * Ao clicar na notificação, abre o ChatConversationModal (igual ao Kanban).
 *
 * RELEASE: 2026-07-24 v1.1.0 - Visual premium: gradiente, borda, shadow, hover elevate
 *          alinhado aos KPI cards e demais botões do header.
 *
 * Uso:
 * <NotificationBell />
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAgentNotifications, AgentNotification } from '@/hooks/useAgentNotifications';
import { NotificationCenterSheet } from './NotificationCenterSheet';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { ChargeDetailsModal } from '@/components/financial/ChargeDetailsModal';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import type { FinanceiroCobranca } from '@/hooks/useFinanceiroCobrancas';
import type { DealModalTab } from '@/components/crm/deals/modal/DealModalTabs';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { showToast } from '@/lib/toast';

// Chaves do módulo Tarefas em permission_profile_items.
// Não existe uma chave agregada 'tasks' — qualquer uma destas concede acesso ao módulo.
const TASKS_MENU_KEYS = [
  'tasks_dashboard',
  'tasks_kanban',
  'tasks_list',
  'tasks_categories',
  'tasks_phases',
  'tasks_classifications',
  'tasks_checklist_templates',
] as const;


/**
 * Props do componente
 */
interface NotificationBellProps {
  /** Classes CSS adicionais */
  className?: string;
}

export function NotificationBell({ className }: NotificationBellProps) {
  const navigate = useNavigate();
  const {
    unreadCount,
    markAsRead,
  } = useAgentNotifications();

  // Controla a abertura da Sheet do Centro de Notificações
  const [centerOpen, setCenterOpen] = useState(false);

  // Permissões do usuário para controle de acesso aos modais
  const { canView } = usePermissions();

  // Estado para controlar o modal de conversa
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  
  // Estado para controlar o modal de negócio (menções em atividades / contrato assinado)
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

  // Estado para controlar qual aba abrir no modal de negócio
  const [selectedDealInitialTab, setSelectedDealInitialTab] = useState<DealModalTab | undefined>(undefined);

  // v1.1.0: step de cadência a pré-selecionar quando initialTab='cadence'
  const [selectedDealInitialStepId, setSelectedDealInitialStepId] = useState<string | null>(null);

  // Estado para controlar o modal de cobrança (pagamento recebido)
  const [selectedCobrancaId, setSelectedCobrancaId] = useState<string | null>(null);

  // Estado para controlar o modal de detalhes da tarefa (notificações do módulo de Tarefas)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  /**
   * Handler para clique na notificação
   * - Menções: navega para Feed de Atividades
   * - Conversas: abre modal de chat
   */
  const handleNavigateToConversation = useCallback((notification: AgentNotification) => {
    // Marks como lida
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Menção em atividade → abre modal do negócio se disponível
    if (notification.type === 'activity_mention') {
      const dealId = notification.metadata?.deal_id;
      if (dealId) {
        // Verifica permissão antes de abrir modal do negócio
        if (!canView('crm_deals')) {
          showToast.warning('Sem permissão para acessar detalhes do negócio.');
          return;
        }
        // Abre modal do negócio na aba padrão (summary)
        setSelectedDealInitialTab(undefined);
        setSelectedDealId(dealId);
        return;
      }
      // Fallback: navega para feed de atividades se não tiver deal_id
      navigate('/tenant/crm/activities');
      return;
    }

    // Assinatura de contrato → abre modal do negócio direto na aba Contratos
    if (notification.type === 'contract_signed') {
      const dealId = notification.metadata?.deal_id;
      if (dealId) {
        // Verifica permissão antes de abrir modal do negócio
        if (!canView('crm_deals')) {
          showToast.warning('Sem permissão para acessar detalhes do negócio.');
          return;
        }
        setSelectedDealInitialTab('contracts'); // Abre diretamente na aba Contratos
        setSelectedDealId(dealId);
        return;
      }
      // Sem deal_id: nenhuma ação disponível
      return;
    }

    // Agendamento online → abre modal do negócio vinculado
    if (notification.type === 'scheduling_booking') {
      const dealId = notification.metadata?.deal_id;
      if (dealId) {
        // Verifica permissão antes de abrir modal do negócio
        if (!canView('crm_deals')) {
          showToast.warning('Sem permissão para acessar detalhes do negócio.');
          return;
        }
        setSelectedDealInitialTab(undefined);
        setSelectedDealId(dealId);
        return;
      }
      // Sem deal_id: sem ação (booking sem negócio vinculado)
      return;
    }

    // Pagamento recebido → abre modal de detalhes da cobrança
    if (notification.type === 'payment_received') {
      const cobrancaId = notification.metadata?.cobranca_id;
      if (cobrancaId) {
        setSelectedCobrancaId(cobrancaId);
        return;
      }
      // Sem cobranca_id: sem ação
      return;
    }

    // Tarefas (atribuição ou novo andamento) → abre o modal de detalhes da tarefa
    if (notification.type === 'task_assigned' || notification.type === 'task_comment_added' || notification.type === 'task_comment_mention') {
      const taskId = notification.metadata?.task_id;
      if (taskId) {
        // Não existe chave 'tasks' agregada em permission_profile_items.
        // Aceita qualquer chave do módulo Tarefas (mesma lógica de TasksDefaultRedirect).
        // A visibilidade fina da tarefa específica é validada server-side por can_user_view_task.
        const hasTasksAccess = TASKS_MENU_KEYS.some(k => canView(k));
        if (!hasTasksAccess) {
          showToast.warning('Sem permissão para acessar detalhes da tarefa.');
          return;
        }
        setSelectedTaskId(taskId);
        return;
      }
      // Sem task_id: sem ação
      return;
    }

    // Atribuição de vendedor em negócio → abre modal do negócio
    if (notification.type === 'deal_seller_assigned') {
      const dealId = notification.metadata?.deal_id;
      if (dealId) {
        if (!canView('crm_deals')) {
          showToast.warning('Sem permissão para acessar detalhes do negócio.');
          return;
        }
        setSelectedDealInitialTab(undefined);
        setSelectedDealId(dealId);
        return;
      }
      return;
    }

    // v1.1.0: Atividade da cadência atrasada → abre modal do negócio na aba Cadência
    if (notification.type === ('cadence_step_overdue' as AgentNotification['type'])) {
      const meta = (notification.metadata ?? {}) as Record<string, unknown>;
      const dealId = meta.deal_id as string | undefined;
      const stepId = (meta.step_id as string | undefined) ?? null;
      if (dealId) {
        if (!canView('crm_deals')) {
          showToast.warning('Sem permissão para acessar detalhes do negócio.');
          return;
        }
        setSelectedDealInitialTab('cadence');
        setSelectedDealInitialStepId(stepId);
        setSelectedDealId(dealId);
        return;
      }
      return;
    }

    // Compartilhamento/permissão de calendário → abre a tela de configurações
    if (
      notification.type === 'calendar_shared' ||
      notification.type === 'calendar_permission_updated' ||
      notification.type === 'calendar_permission_revoked'
    ) {
      const calendarId = notification.metadata?.calendar_id as string | undefined;
      const url = calendarId
        ? `/tenant/calendar/settings?cal=${calendarId}`
        : '/tenant/calendar/settings';
      navigate(url);
      return;
    }

    // Conversa → abre modal
    if (notification.conversation_id) {
      setSelectedConversationId(notification.conversation_id);
    }
  }, [markAsRead, navigate]);

  // Formata o badge para exibição
  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);
  const hasUnread = unreadCount > 0;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCenterOpen(true)}
            className={cn(
              'relative h-9 w-9 rounded-xl border border-border/70 bg-gradient-to-br from-card to-muted/40 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md hover:bg-gradient-to-br hover:from-card hover:to-muted/60 active:translate-y-0 active:shadow-sm focus-visible:ring-2 focus-visible:ring-primary/50',
              hasUnread && 'border-primary/40 ring-2 ring-primary/20 animate-pulse',
              className,
            )}
            aria-label={`Notificações${hasUnread ? ` (${unreadCount} não lidas)` : ''}`}
          >
            <Bell className={cn('h-4 w-4 transition-colors', hasUnread ? 'text-primary' : 'text-muted-foreground')} />
            {hasUnread && (
              <span
                className={cn(
                  'absolute -top-1 -right-1 flex items-center justify-center',
                  'min-w-[18px] h-[18px] px-1',
                  'bg-destructive text-destructive-foreground',
                  'text-[10px] font-semibold rounded-full',
                  'animate-in zoom-in-50 duration-200',
                )}
              >
                {badgeText}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>
            {hasUnread
              ? `${unreadCount} notificação${unreadCount > 1 ? 'ões' : ''} não lida${unreadCount > 1 ? 's' : ''}`
              : 'Notificações'}
          </p>
        </TooltipContent>
      </Tooltip>

      {/* Sheet lateral premium estilo Pipedrive */}
      <NotificationCenterSheet
        open={centerOpen}
        onOpenChange={setCenterOpen}
        onOpenEntity={handleNavigateToConversation}
      />

      {/* Modal de conversa - mesmo componente usado no Kanban */}
      <ChatConversationModal
        conversationId={selectedConversationId}
        open={!!selectedConversationId}
        onOpenChange={(open) => !open && setSelectedConversationId(null)}
      />

      {/* Modal de detalhes do negócio - para menções em atividades e contratos assinados */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={!!selectedDealId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedDealId(null);
            setSelectedDealInitialTab(undefined); // Limpa a aba ao fechar
            setSelectedDealInitialStepId(null);   // v1.1.0: limpa o step
          }
        }}
        initialTab={selectedDealInitialTab}
        initialStepId={selectedDealInitialStepId}
      />

      {/* Modal de detalhes da cobrança - para notificações de pagamento recebido */}
      <ChargeDetailsModal
        cobranca={selectedCobrancaId ? { id: selectedCobrancaId } as FinanceiroCobranca : null}
        open={!!selectedCobrancaId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedCobrancaId(null);
          }
        }}
      />

      {/* Modal de detalhes da tarefa - para notificações de atribuição e novo andamento */}
      <TaskDetailsModal
        taskId={selectedTaskId}
        open={!!selectedTaskId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTaskId(null);
          }
        }}
      />
    </>
  );
}
